import { BET_SLIP_ERRORS } from '../../constants/betSlipErrors';
import { acceptOddChangeEnum, EVENT_TYPE_ENUM, ticketTypeEnum } from '../../constants/enums';
import {
    BET_SLIP_TABS,
    MAX_BET_LINE_COUNT,
    STAKE_VALUES,
    MINIMUM_ODD_VALUE,
} from '../../constants/defaultValues';
import * as types from '../actions/types';
import betSlipCalculator from '../../utils/betSlipCalculator';
import { formatMessage } from '../../utils/translation';
import { isESportBetLine, isOnlyESport } from '../../utils/helpers';

const initialDisabledTabs = [BET_SLIP_TABS.combo.id, BET_SLIP_TABS.system.id];
const initialStakes = {
    [BET_SLIP_TABS.single.id]: STAKE_VALUES.min,
    [BET_SLIP_TABS.combo.id]: STAKE_VALUES.min,
    [BET_SLIP_TABS.system.id]: STAKE_VALUES.min,
};
const initialValidations = {
    single: [],
    combo: [],
    system: [],
    common: [],
};

const initialState = {
    activeTab: BET_SLIP_TABS.single.id,
    disabledTabs: [...initialDisabledTabs],
    selectedOddChangeDecision: acceptOddChangeEnum.YES,
    betLines: [],
    stakes: { ...initialStakes },
    isFetchingEvents: false,
    isTicketSubmitting: false,
    placedTicketDetails: null,
    validations: { ...initialValidations },
    placedBetErrors: [],
    selectedCombinations: [],
    stakeLimits: {
        sportMinimum: STAKE_VALUES.min,
        eSportMinimum: STAKE_VALUES.min,
    },
    isMaxStakeLoading: false,
    requestId: null,
};

export default function (state = initialState, action) {
    const { type, payload } = action;

    switch (type) {
        case types.CHANGE_BET_SLIP_ACTIVE_TAB: {
            let newBetlines = state.betLines;

            if (payload !== BET_SLIP_TABS.system.id) {
                newBetlines = newBetlines.map(betLine => ({
                    ...betLine,
                    banco: false,
                }));
            }

            return {
                ...state,
                activeTab: payload,
                selectedCombinations: [],
                betLines: newBetlines,
                placedBetErrors: [],
            };
        }
        case types.CHANGE_BET_SLIP_DISABLED_TABS: {
            return {
                ...state,
                disabledTabs: payload,
            };
        }
        case types.RESET_BET_SLIP_DISABLED_TABS: {
            return {
                ...state,
                disabledTabs: [...initialDisabledTabs],
            };
        }
        case types.CHANGE_BET_SLIP_ODD_CHANGE_DECISION: {
            return {
                ...state,
                selectedOddChangeDecision: payload,
                placedBetErrors: [],
            };
        }
        case types.ADD_BET_SLIP_BET_LINE: {
            const { eventId, marketId, outcomeId } = payload;
            const found = state.betLines.find(betLine => {
                return (
                    betLine.eventId === eventId &&
                    betLine.marketId === marketId &&
                    betLine.outcomeId === outcomeId
                );
            });
            if (found) return state;

            const newBetLines = [...state.betLines, payload];
            const newDisabledTabs = getDisabledTabs({ betLines: newBetLines });
            const newActiveTab = getActiveTab({
                betLines: newBetLines,
                activeTab: state.activeTab,
            });

            return {
                ...state,
                betLines: newBetLines,
                disabledTabs: newDisabledTabs,
                activeTab: newActiveTab,
                placedBetErrors: [],
                selectedCombinations: [],
            };
        }
        case types.DELETE_BET_SLIP_BET_LINE: {
            const { eventId, marketId, outcomeId } = payload;
            const newBetLines = state.betLines.filter(
                betLine =>
                    !(
                        betLine.eventId === eventId &&
                        betLine.marketId === marketId &&
                        betLine.outcomeId === outcomeId
                    ),
            );

            if (newBetLines.length === 0) {
                return {
                    ...state,
                    ...getInitialBetSlipState(state),
                };
            }

            const newDisabledTabs = getDisabledTabs({ betLines: newBetLines });
            const newActiveTab = getActiveTab({
                betLines: newBetLines,
                activeTab: state.activeTab,
            });

            return {
                ...state,
                betLines: newBetLines,
                disabledTabs: newDisabledTabs,
                activeTab: newActiveTab,
                placedBetErrors: [],
                selectedCombinations: [],
            };
        }
        case types.UPDATE_BET_SLIP_BET_LINES: {
            return {
                ...state,
                betLines: payload,
                placedBetErrors: [],
            };
        }
        case types.CHANGE_BET_SLIP_BET_LINE_STAKE: {
            const { eventId, marketId, outcomeId, stake } = payload;
            const newBetLines = state.betLines.map(betLine => {
                if (
                    betLine.eventId === eventId &&
                    betLine.marketId === marketId &&
                    betLine.outcomeId === outcomeId
                ) {
                    return { ...betLine, stake };
                }

                return betLine;
            });

            return {
                ...state,
                betLines: newBetLines,
                placedBetErrors: [],
            };
        }
        case types.BET_SLIP_STAKES_CHANGED: {
            const { type, stake } = payload;
            const newStakes = { ...state.stakes };
            newStakes[type] = stake;

            return {
                ...state,
                stakes: newStakes,
                placedBetErrors: [],
            };
        }
        case types.BET_SLIP_EVENTS_LOADING: {
            return { ...state, isFetchingEvents: payload };
        }
        case types.BET_SLIP_EVENTS_LOADED: {
            const newBetLines = state.betLines.map(betLine => {
                const id = `${betLine.marketId}${betLine.outcomeId}`;
                const payloadEvent = payload[id];

                if (payloadEvent) {
                    return {
                        ...betLine,
                        ...payloadEvent,
                        mbn: payloadEvent.mbn || betLine.mbn,
                        changeType: '',
                    };
                } else {
                    return {
                        ...betLine,
                        changeType: '',
                        closed: true,
                    };
                }
            });

            return {
                ...state,
                isFetchingEvents: false,
                betLines: newBetLines,
            };
        }
        case types.PLACE_BET_REQUEST: {
            return {
                ...state,
                isTicketSubmitting: true,
                placedBetErrors: [],
            };
        }
        case types.PLACE_BET_FAILED:
            return {
                ...state,
                isTicketSubmitting: false,
                placedBetErrors: payload,
            };
        case types.BET_SLIP_PLACED_TICKET_CHANGED: {
            return {
                ...state,
                placedTicketDetails: payload,
                isTicketSubmitting: false,
                placedBetErrors: [],
            };
        }
        case types.CLEAR_BET_SLIP_PLACE_ERRORS: {
            return {
                ...state,
                placedBetErrors: [],
            };
        }
        case types.BET_SLIP_COMBINATIONS_CHANGED: {
            return {
                ...state,
                selectedCombinations: payload,
                placedBetErrors: [],
            };
        }
        case types.SET_BET_BANCO: {
            const { eventId, marketId, outcomeId } = payload;
            const newBetLines = state.betLines.map(betLine => {
                if (
                    betLine.eventId === eventId &&
                    betLine.marketId === marketId &&
                    betLine.outcomeId === outcomeId
                ) {
                    return { ...betLine, banco: !betLine.banco };
                }

                return betLine;
            });

            return {
                ...state,
                betLines: newBetLines,
                placedBetErrors: [],
                selectedCombinations: [],
            };
        }
        case types.CHANGE_BET_STAKE_LIMITS: {
            const stakeLimits = {
                ...state.stakeLimits,
                ...payload,
            };

            const stakes = {
                [BET_SLIP_TABS.single.id]: payload.sportMinimum || STAKE_VALUES.min,
                [BET_SLIP_TABS.combo.id]: payload.sportMinimum || STAKE_VALUES.min,
                [BET_SLIP_TABS.system.id]: payload.sportMinimum || STAKE_VALUES.min,
            };

            return {
                ...state,
                stakeLimits,
                stakes,
            };
        }
        case types.BET_SLIP_VALIDATIONS_CHANGED:
            return {
                ...state,
                validations: payload,
            };

        case types.CLEAR_BET_SLIP:
            return {
                ...state,
                ...getInitialBetSlipState(state),
            };
        case types.BET_SLIP_MAX_STAKE_LOADING:
            return {
                ...state,
                isMaxStakeLoading: payload,
            };
        case types.BET_SLIP_REQUEST_ID_CHANGED:
            return {
                ...state,
                requestId: payload,
            };
        default:
            return state;
    }
}

const getInitialBetSlipState = state => {
    return {
        betLines: [],
        activeTab: BET_SLIP_TABS.single.id,
        disabledTabs: [...initialDisabledTabs],
        selectedOddChangeDecision: acceptOddChangeEnum.YES,
        stakes: {
            [BET_SLIP_TABS.single.id]: state.stakeLimits.sportMinimum || STAKE_VALUES.min,
            [BET_SLIP_TABS.combo.id]: state.stakeLimits.sportMinimum || STAKE_VALUES.min,
            [BET_SLIP_TABS.system.id]: state.stakeLimits.sportMinimum || STAKE_VALUES.min,
        },
        isFetchingEvents: false,
        isTicketSubmitting: false,
        placedTicketDetails: null,
        placedBetErrors: [],
        validations: { ...initialValidations },
        selectedCombinations: [],
    };
};

export const validateBetLines = ({
    activeTab,
    betLines,
    currentBalance,
    selectedCombinations = [],
    stakeLimits,
    stakes,
    validations,
    isVoucherMode,
}) => {
    let newValidations = { ...validations };

    if (betLines.length === 0) {
        return { ...initialValidations };
    }

    const addValidation = (validations, newValidation) => {
        const isAlreadyAdded = validations.find(v => v.key === newValidation.key);

        if (isAlreadyAdded) return validations;

        return [...validations, newValidation];
    };

    const deleteValidation = (validations, key) => {
        return validations.filter(v => v.key !== key);
    };

    const validateSingle = ({ mbns, validations, mbnValidation }) => {
        const hasHigherMbnThanOne = mbns.some(mbn => mbn !== 1);

        let newValidations = [...validations];

        if (hasHigherMbnThanOne) {
            newValidations = addValidation(newValidations, mbnValidation);
        } else {
            newValidations = deleteValidation(newValidations, mbnValidation.key);
        }

        return newValidations;
    };

    const validateCombo = ({ mbns, validations, mbnValidation }) => {
        const hasOneMbn = mbns.some(mbn => mbn === 1);

        let newValidations = [...validations];

        if (!hasOneMbn) {
            const maxMbn = Math.max(...mbns);

            if (mbns.length < maxMbn) {
                newValidations = addValidation(newValidations, mbnValidation);
            } else {
                newValidations = deleteValidation(newValidations, mbnValidation.key);
            }
        } else {
            newValidations = deleteValidation(newValidations, mbnValidation.key);
        }

        return newValidations;
    };

    const validateStake = ({ hasInvalidMinimumStake, validations, invalidStakeAmounts }) => {
        const minimumStakeError = { ...BET_SLIP_ERRORS.minimumStakeError };
        minimumStakeError.message = formatMessage(minimumStakeError.message, {
            amount: invalidStakeAmounts.minimum,
        });

        if (hasInvalidMinimumStake) {
            validations = addValidation(validations, minimumStakeError);
        } else {
            validations = deleteValidation(validations, minimumStakeError.key);
        }

        return validations;
    };

    // const validateMaxWin = ({ validations, toWin }) => {
    //     if (toWin > 250000) {
    //         return addValidation(validations, BET_SLIP_ERRORS.maximumWinError);
    //     } else {
    //         return deleteValidation(validations, BET_SLIP_ERRORS.maximumWinError.key);
    //     }
    // };

    const validateMinOdd = ({ validations, hasInvalidMinimumOdd }) => {
        if (hasInvalidMinimumOdd) {
            return addValidation(validations, BET_SLIP_ERRORS.minimumOddError);
        } else {
            return deleteValidation(validations, BET_SLIP_ERRORS.minimumOddError.key);
        }
    };

    const validateMinTotalOdd = ({ validations, hasInvalidMinimumTotalOdd }) => {
        if (hasInvalidMinimumTotalOdd) {
            return addValidation(validations, BET_SLIP_ERRORS.minimumTotalOddError);
        } else {
            return deleteValidation(validations, BET_SLIP_ERRORS.minimumTotalOddError.key);
        }
    };

    const validateCombinations = ({ validations, betLines, shouldCheckCombination }) => {
        const regularBetLines = [];
        const outrightBetLines = [];

        betLines.forEach(betLine => {
            if (betLine.eventType === EVENT_TYPE_ENUM.regular) {
                regularBetLines.push(betLine);
            } else {
                outrightBetLines.push(betLine);
            }
        });

        const uniqueRegularBetLines = new Set(regularBetLines.map(b => b.eventId));
        const uniqueOutrightBetLines = new Set(outrightBetLines.map(b => b.marketId));

        if (
            shouldCheckCombination &&
            (regularBetLines.length !== uniqueRegularBetLines.size ||
                outrightBetLines.length !== uniqueOutrightBetLines.size)
        ) {
            validations.common = addValidation(
                validations.common,
                BET_SLIP_ERRORS.combinationError,
            );
        } else {
            validations.common = deleteValidation(
                validations.common,
                BET_SLIP_ERRORS.combinationError.key,
            );
        }
    };

    const validateEventTypes = ({ validations, betLines, shouldCheckCombination }) => {
        let eventTypeCount = 0;

        if (betLines.some(b => isESportBetLine(b))) eventTypeCount++;
        if (betLines.some(b => !isESportBetLine(b) && b.eventType === EVENT_TYPE_ENUM.regular))
            eventTypeCount++;
        if (betLines.some(b => b.eventType === EVENT_TYPE_ENUM.outright)) eventTypeCount++;

        if (shouldCheckCombination && eventTypeCount > 1) {
            validations.common = addValidation(
                validations.common,
                BET_SLIP_ERRORS.eventTypeCombinationError,
            );
        } else {
            validations.common = deleteValidation(
                validations.common,
                BET_SLIP_ERRORS.eventTypeCombinationError.key,
            );
        }
    };

    const validateLiveBet = ({ validations, betLines }) => {
        if (isVoucherMode && betLines.some(b => b.isLive && !isESportBetLine(b))) {
            validations.common = addValidation(validations.common, BET_SLIP_ERRORS.liveBetError);
        } else {
            validations.common = deleteValidation(
                validations.common,
                BET_SLIP_ERRORS.liveBetError.key,
            );
        }
    };

    const { totalStake, totalOdds, toWin, allSystemCombinationOdds } =
        betSlipCalculator.calculateTicketDetails({
            betLines,
            activeTab,
            stakes,
            selectedCombinations,
        });

    const mbns = betLines.map(b => b.mbn);

    const shouldCheckCombination = Math.max(...mbns) > 1 || activeTab !== BET_SLIP_TABS.single.id;

    validateCombinations({
        validations: newValidations,
        betLines,
        shouldCheckCombination,
    });
    validateEventTypes({
        validations: newValidations,
        betLines,
        shouldCheckCombination,
    });
    validateLiveBet({
        validations: newValidations,
        betLines,
    });

    const hasInvalidMinimumOdd = betLines.some(betLine => betLine.odd < MINIMUM_ODD_VALUE);
    newValidations.common = validateMinOdd({
        validations: newValidations.common,
        hasInvalidMinimumOdd,
    });

    const invalidStakeAmounts = {
        minimum: null,
    };

    const isESports = isOnlyESport(betLines);
    const minimumStake = isESports ? stakeLimits.eSportMinimum : stakeLimits.sportMinimum;

    if (activeTab === BET_SLIP_TABS.single.id) {
        newValidations.single = validateSingle({
            mbns,
            validations: newValidations.single,
            mbnValidation: BET_SLIP_ERRORS.singleMbnError,
        });

        let hasInvalidMinimumStake;

        if (currentBalance - totalStake > 0 && currentBalance - totalStake < minimumStake) {
            invalidStakeAmounts.minimum = currentBalance;
            hasInvalidMinimumStake = true;
        } else {
            hasInvalidMinimumStake = betLines.some(betLine => {
                if (isESportBetLine(betLine)) {
                    if (betLine.stake < stakeLimits.eSportMinimum) {
                        invalidStakeAmounts.minimum = stakeLimits.eSportMinimum;
                        return true;
                    }
                } else {
                    if (betLine.stake < stakeLimits.sportMinimum) {
                        invalidStakeAmounts.minimum = stakeLimits.sportMinimum;
                        return true;
                    }
                }

                return false;
            });
        }

        newValidations.single = validateStake({
            hasInvalidMinimumStake,
            validations: newValidations.single,
            invalidStakeAmounts,
        });
        // newValidations.single = validateMaxWin({
        //     validations: newValidations.single,
        //     toWin,
        // });

        newValidations.combo = [];
        newValidations.system = [];
    } else if (activeTab === BET_SLIP_TABS.combo.id) {
        newValidations.single = [];
        newValidations.combo = validateCombo({
            mbns,
            validations: newValidations.combo,
            mbnValidation: BET_SLIP_ERRORS.comboMbnError,
        });

        let hasInvalidMinimumStake = false;

        if (currentBalance - totalStake > 0 && currentBalance - totalStake < minimumStake) {
            invalidStakeAmounts.minimum = currentBalance;
            hasInvalidMinimumStake = true;
        } else {
            if (stakes[BET_SLIP_TABS.combo.id] < minimumStake) {
                invalidStakeAmounts.minimum = minimumStake;
                hasInvalidMinimumStake = true;
            }
        }

        newValidations.combo = validateStake({
            hasInvalidMinimumStake,
            validations: newValidations.combo,
            invalidStakeAmounts,
        });
        // newValidations.combo = validateMaxWin({
        //     validations: newValidations.combo,
        //     toWin,
        // });
        newValidations.combo = validateMinTotalOdd({
            validations: newValidations.combo,
            hasInvalidMinimumTotalOdd: totalOdds < MINIMUM_ODD_VALUE,
        });

        newValidations.system = [];
    } else if (activeTab === BET_SLIP_TABS.system.id) {
        if (selectedCombinations.length > 0 && selectedCombinations.includes(2)) {
            const mbns = betLines.filter(b => !b.banco).map(b => b.mbn);

            const combinations = [];

            for (let i = 0; i < mbns.length; i++) {
                for (let j = i + 1; j < mbns.length; j++) {
                    combinations.push([mbns[i], mbns[j]]);
                }
            }

            const hasInvalidCombination = combinations.some(combination => {
                const validatedCombination = validateCombo({
                    mbns: combination,
                    validations: newValidations.system,
                    mbnValidation: BET_SLIP_ERRORS.mbnCombinationsError,
                });

                return !!validatedCombination.find(
                    c => c.key === BET_SLIP_ERRORS.mbnCombinationsError.key,
                );
            });

            if (hasInvalidCombination) {
                newValidations.system = addValidation(
                    newValidations.system,
                    BET_SLIP_ERRORS.mbnCombinationsError,
                );
            } else {
                newValidations.system = deleteValidation(
                    newValidations.system,
                    BET_SLIP_ERRORS.mbnCombinationsError.key,
                );
            }
        } else {
            newValidations.system = deleteValidation(
                newValidations.system,
                BET_SLIP_ERRORS.mbnCombinationsError.key,
            );
        }

        let hasInvalidMinimumStake = false;

        if (selectedCombinations.length > 0) {
            if (currentBalance - totalStake > 0 && currentBalance - totalStake < minimumStake) {
                invalidStakeAmounts.minimum = currentBalance;
                hasInvalidMinimumStake = true;
            } else {
                if (totalStake < minimumStake) {
                    invalidStakeAmounts.minimum = minimumStake;
                    hasInvalidMinimumStake = true;
                }
            }
        }

        newValidations.single = [];
        newValidations.combo = [];
        newValidations.system = validateStake({
            hasInvalidMinimumStake,
            validations: newValidations.system,
            invalidStakeAmounts,
        });
        // newValidations.system = validateMaxWin({
        //     validations: newValidations.system,
        //     toWin,
        // });
        newValidations.system = validateMinTotalOdd({
            validations: newValidations.system,
            hasInvalidMinimumTotalOdd: Math.min(allSystemCombinationOdds) < MINIMUM_ODD_VALUE,
        });
    }

    if (betLines.length > MAX_BET_LINE_COUNT) {
        const maxBetLineCountError = {
            ...BET_SLIP_ERRORS.maximumBetLineCountError,
        };
        maxBetLineCountError.message = formatMessage(maxBetLineCountError.message, {
            count: MAX_BET_LINE_COUNT,
        });
        newValidations.common = addValidation(newValidations.common, maxBetLineCountError);
    } else {
        newValidations.common = deleteValidation(
            newValidations.common,
            BET_SLIP_ERRORS.maximumBetLineCountError.key,
        );
    }

    return newValidations;
};

const getDisabledTabs = ({ betLines }) => {
    const mbns = betLines.map(b => b.mbn);
    const disabledTabs = [];

    if (mbns.length < 3) {
        disabledTabs.push(BET_SLIP_TABS.system.id);
    }

    if (mbns.length < 2) {
        disabledTabs.push(BET_SLIP_TABS.combo.id);
    }

    if (mbns.find(mbn => mbn !== 1) && mbns.length > 1) {
        disabledTabs.push(BET_SLIP_TABS.single.id);
    }

    return disabledTabs;
};

const getActiveTab = ({ betLines, activeTab }) => {
    const mbns = betLines.map(b => b.mbn);
    let newActiveTab = activeTab;

    if (activeTab === BET_SLIP_TABS.single.id && mbns.length > 1) {
        newActiveTab = BET_SLIP_TABS.combo.id;
    } else if (activeTab === BET_SLIP_TABS.combo.id && mbns.length < 2) {
        newActiveTab = BET_SLIP_TABS.single.id;
    } else if (activeTab === BET_SLIP_TABS.system.id && mbns.length < 3) {
        newActiveTab = BET_SLIP_TABS.combo.id;
    }

    return newActiveTab;
};
